import React from 'react';
import { Box, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import useUserAuth from '../../../../utils/useUserAuth';
import { formatSportTypes } from './sportTypeLabel';
import AccessDoorRow from './AccessDoorRow';

import {
  TicketCard,
  ExpiredCardWrapper,
  CardInfoSection,
  HeaderRow,
  HeaderLeft,
  ClassTypeLabel,
  TicketTitle,
  TypeLabel,
  InfoRow,
  InfoLeft,
  InfoLabel,
  InfoValue,
  HighlightText,
  ExpiredLabel,
  UpcomingLabel,
  SubscriptionChip,
} from './CourseTicketCardStyled';
import { usageBranchLabel, canEditUseBranches } from '../../../../utils/branchShare';
import { toActionRows } from './cardActions';
import { CardButtonArea, CardButton } from './PassCardStyled';

const URGENT_DAYS_THRESHOLD = 10;

const PassCard = ({ pass, isExpired, onPeriodChange, onFreeze, onCancelSubscription, onUseBranchEdit, onChangeHistoryOpen }) => {
  const canPeriodChange = useUserAuth('MEMBER_PASS_PERIOD_CHANGE');
  const canSuspend = useUserAuth('MEMBER_PASS_SUSPEND');
  const isStopScheduled = pass.displayStatus === 'STOP_SCHEDULED' || (pass.status === 'ACTIVE' && pass.stoppedDate && dayjs(pass.stoppedDate).startOf('day').isAfter(dayjs().startOf('day')));
  const isStopped = pass.status === 'STOPPED' && !isStopScheduled;
  const isSubscription = !!pass.subscriptionYn;
  const isBillingCancelled = pass.billingStatus === 'CANCELLED'; // 구독 해지됨(이용권은 만료일까지 유지)
  // 빌링키 미등록(PAUSED/FAILED)인데 이용권은 유효 → 회원앱과 동일하게 '결제수단 미등록'.
  // 백엔드(MemberCoursePassInfo)가 앱과 동일 규칙으로 내려주는 합성 상태. (필드 없으면 false로 폴백)
  const isPaymentRequired = pass.subscriptionStatus === 'payment_required';
  // 구독제 + 활성: 미해지면 "구독 해지", 해지됐으면 "구독 정보"(상태·이력 확인).
  // 재구독은 회원앱 전용이라 admin에서는 제공하지 않는다.
  const showSubscriptionAction = !isExpired && isSubscription && pass.status === 'ACTIVE';

  // 시작일이 미래면 '사용예정'으로 표시하고 잔여일수는 계산하지 않는다.
  const today = dayjs().startOf('day');
  const isUpcoming = pass.issueDate ? today.isBefore(dayjs(pass.issueDate).startOf('day')) : false;

  const daysRemaining =
    !isUpcoming && pass.expireDate ? dayjs(pass.expireDate).startOf('day').diff(today, 'day') : null;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= URGENT_DAYS_THRESHOLD;

  const periodText =
    pass.usagePeriodType === 'MONTH' ? `${pass.usagePeriod}개월` : `${pass.usagePeriod}일`;

  const sportTypeText = formatSportTypes(pass.sportTypes);

  const usageLabel = usageBranchLabel(pass.branchShare, { forMember: true });
  const showUseBranchEdit = !isExpired && !!onUseBranchEdit && canEditUseBranches(pass.branchShare);

  // 하단 액션 버튼 — 조건에 맞는 것만 모아 2열로 배치한다(홀수면 마지막이 전폭).
  const actions = [
    {
      key: 'period',
      label: '상품 정보 수정',
      onClick: canPeriodChange ? onPeriodChange : undefined,
      tooltip: !canPeriodChange ? '권한이 없습니다' : '',
      dimmed: !canPeriodChange,
    },
    ...(pass?.status !== 'INACTIVE' ? [{
      key: 'stop',
      label: '이용권 중지 설정',
      onClick: canSuspend ? onFreeze : undefined,
      tooltip: !canSuspend ? '권한이 없습니다' : '',
      dimmed: !canSuspend,
      disabled: isExpired && !pass?.isPossibleStop,
    }] : []),
    ...(showSubscriptionAction ? [{
      key: 'subscription',
      label: isBillingCancelled || isPaymentRequired ? '구독 정보' : '구독 해지',
      onClick: onCancelSubscription,
    }] : []),
    ...(showUseBranchEdit ? [{
      key: 'useBranch',
      label: '이용지점 변경',
      onClick: onUseBranchEdit,
    }] : []),
    ...(onChangeHistoryOpen ? [{
      key: 'changeHistory',
      label: '변경 내역 조회',
      onClick: onChangeHistoryOpen,
    }] : []),
  ];
  const actionRows = toActionRows(actions);

  const card = (
    <TicketCard isStopped={!isExpired && isStopped} subscription={isSubscription && !isExpired}>
      <CardInfoSection>
        {/* Row 1: 지점 + [구독중 칩] + 상품명 | 중지 상태 */}
        <HeaderRow>
          <HeaderLeft>
            <ClassTypeLabel isStopped={!isExpired && isStopped}>{pass.branchName}</ClassTypeLabel>
            {isSubscription && (
              <SubscriptionChip
                isStopped={(!isExpired && isStopped) || isBillingCancelled || isPaymentRequired}>
                {isBillingCancelled ? '해지됨' : isPaymentRequired ? '결제수단 미등록' : '구독'}
              </SubscriptionChip>
            )}
            <Tooltip title={`${pass.ticketName} ${periodText}`} arrow componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}>
              <TicketTitle isStopped={!isExpired && isStopped}>
                {pass.ticketName} {periodText}
              </TicketTitle>
            </Tooltip>
          </HeaderLeft>
          {/* 상품종류(이용권/구독권) 라벨은 섹션 제목·구독 칩과 중복이라 제거하고, 중지 상태만 남긴다. */}
          {!isExpired && (isStopScheduled || isStopped) && (
            <TypeLabel isStopped={isStopped} sx={isStopScheduled ? { color: '#EE735A' } : {}}>
              {isStopScheduled ? `중지 예정 (${dayjs(pass.stoppedDate).format('M/D')}~)` : '중지'}
            </TypeLabel>
          )}
        </HeaderRow>

        {/* Row 1.5: 다지점 공유 상품 이용지점(#98) — 비공유면 빈 문자열이라 안 그린다 */}
        {usageLabel && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>이용지점</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{usageLabel}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 2: 운동종목 (다종목 상품은 쉼표로 나열) */}
        {sportTypeText && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>종목</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>{sportTypeText}</InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* 출입문 (전환 지점만 렌더 — #91 회원별 오버라이드) */}
        <AccessDoorRow
          holdingType="PASS"
          holdingId={pass.memberCoursePassId}
          isStopped={!isExpired && isStopped}
        />

        {/* 구독권: 다음 결제일 (해지/미등록이면 -) */}
        {isSubscription && (
          <InfoRow>
            <InfoLeft>
              <InfoLabel isStopped={!isExpired && isStopped}>다음 결제일</InfoLabel>
              <InfoValue isStopped={!isExpired && isStopped}>
                {isBillingCancelled
                  ? '해지됨 (만료일까지 이용 가능)'
                  : isPaymentRequired
                    ? '결제수단 미등록'
                    : pass.nextBillingDate
                      ? pass.nextBillingDate.replaceAll('-', '.')
                      : '-'}
              </InfoValue>
            </InfoLeft>
          </InfoRow>
        )}

        {/* Row 4: 유효기간 + 잔여일수 (이용권은 횟수 개념이 없어 잔여횟수 행 생략) */}
        <InfoRow>
          <InfoLeft>
            <InfoLabel isStopped={!isExpired && isStopped}>유효기간</InfoLabel>
            <InfoValue isStopped={!isExpired && isStopped}>
              {pass.issueDate?.replaceAll('-', '.')} ~ {pass.expireDate?.replaceAll('-', '.')}
            </InfoValue>
          </InfoLeft>
          {isExpired ? (
            <ExpiredLabel>만료</ExpiredLabel>
          ) : isUpcoming ? (
            <UpcomingLabel>사용예정</UpcomingLabel>
          ) : (
            daysRemaining !== null &&
            daysRemaining >= 0 && (
              <HighlightText isStopped={isStopped} urgent={isUrgent}>
                {daysRemaining}일 남음
              </HighlightText>
            )
          )}
        </InfoRow>
      </CardInfoSection>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {actionRows.map((row, rowIndex) => (
          <CardButtonArea key={row.map(action => action.key).join('-')} isStopped={!isExpired && isStopped}>
            {row.map((action, index) => (
              <Tooltip key={action.key} title={action.tooltip ?? ''} arrow>
                <CardButton
                  variant="text"
                  isStopped={!isExpired && isStopped}
                  noBorderTop={rowIndex > 0}
                  noBorderRight={row.length === 2 && index === 0}
                  disabled={action.disabled}
                  onClick={action.onClick}
                  style={action.dimmed ? { opacity: 0.5, cursor: 'default' } : {}}>
                  {action.label}
                </CardButton>
              </Tooltip>
            ))}
          </CardButtonArea>
        ))}
      </Box>
    </TicketCard>
  );

  if (isExpired) {
    return <ExpiredCardWrapper>{card}</ExpiredCardWrapper>;
  }

  return <Box>{card}</Box>;
};

export default PassCard;
